import { apiClient } from '@/api/client';
import type {
  GetParentCaseShortlistsResponse,
  GetTutorShortlistsResponse,
  GetApplicationsParams,
  ParentCaseShortlistData,
  ParentShortlistRespondRequest,
  ParentShortlistRespondResponse,
  TutorShortlistData,
  TutorShortlistRespondRequest,
  TutorShortlistRespondResponse,
} from '@/types/api';

/**
 * GET /api/shortlists/my-cases
 * Get all shortlist invitations for the current tutor with pagination
 */
export const getMyShortlists = async (
  params?: GetApplicationsParams
): Promise<GetTutorShortlistsResponse['data']> => {
  const response = await apiClient.get<GetTutorShortlistsResponse>('/shortlists/my-cases', {
    params,
  });

  if (!response.data.success) {
    throw new Error('Failed to fetch shortlisted cases');
  }

  return response.data.data;
};

/**
 * PATCH /api/shortlists/:shortlistId/respond
 * Respond to an admin shortlist invitation
 */
export const respondToShortlist = async (
  shortlistId: string,
  request: TutorShortlistRespondRequest
): Promise<TutorShortlistData> => {
  const response = await apiClient.patch<TutorShortlistRespondResponse>(
    `/shortlists/${shortlistId}/respond`,
    request
  );

  if (!response.data.success) {
    throw new Error('Failed to respond to shortlisted case');
  }

  return response.data.data;
};

/**
 * GET /api/shortlists/cases/:caseId
 * Get shortlisted tutors for the current parent's own case
 */
export const getParentCaseShortlists = async (
  caseId: string,
  params?: GetApplicationsParams
): Promise<GetParentCaseShortlistsResponse['data']> => {
  const response = await apiClient.get<GetParentCaseShortlistsResponse>(
    `/shortlists/cases/${caseId}`,
    {
      params,
    }
  );

  if (!response.data.success) {
    throw new Error('Failed to fetch shortlisted tutors');
  }

  return response.data.data;
};

/**
 * PATCH /api/shortlists/:shortlistId/parent-respond
 * Accept or reject a shortlisted tutor for the current parent's own case
 */
export const respondToParentShortlist = async (
  shortlistId: string,
  request: ParentShortlistRespondRequest
): Promise<ParentCaseShortlistData> => {
  const response = await apiClient.patch<ParentShortlistRespondResponse>(
    `/shortlists/${shortlistId}/parent-respond`,
    request
  );

  if (!response.data.success) {
    throw new Error('Failed to update shortlisted tutor');
  }

  return response.data.data;
};
